/**
 * fitScoringEngine.js — objective fit, separate from hard eligibility.
 *
 * Scores how well each surviving product matches the borrower's stated goals
 * using the workbook's term-fit columns and the Term Strategy Matrix rules.
 * A high fit score can never rescue an eligibility failure — the
 * recommendation engine only ranks products that already survived.
 *
 * Encoded guardrails (from the workbook and the design brief):
 *  - Short expected hold NEVER equals ARM by itself. ARMs get fit only when
 *    the hold fits inside the initial fixed period with ~a one-year buffer,
 *    the borrower tolerates reset risk, and live pricing shows benefit.
 *  - Shorter terms only rank up when the required payment passes the
 *    affordability guardrail (comfort payment / income screen).
 *  - Specialty (Non-QM) products are penalized so standard agency execution
 *    wins when outcomes are materially equivalent.
 */
import { screeningMonthlyPI } from './pricingProvider.js';

const level = (fit) => (fit && fit.score != null ? fit.score : null);
/** (0–5 fit level − 3) × weight → signed contribution. */
const scaled = (fit, weight) => {
  const s = level(fit);
  return s == null ? 0 : (s - 3) * weight;
};

/**
 * @param {import('./borrowerProfile').BorrowerProfile} p
 * @param {object} evaluation eligibility evaluation ({ product, status, cautions, ... })
 * @param {object} pricingProvider
 * @returns {{ fitScore, confidence, fitReasons, fitCautions, pricingRequired }}
 */
export function scoreFit(p, evaluation, pricingProvider) {
  const prod = evaluation.product;
  const { structure: s, routing: r } = prod;
  const fit = r.fit;
  let score = 50;
  const reasons = [];
  const cautions = [...evaluation.cautions];
  let pricingRequired = false;

  // ── Borrower objective vs workbook fit columns ────────────────
  const goal = p.primaryMortgageGoal;
  if (goal === 'lowest-payment') {
    score += scaled(fit.monthlyPayment, 6);
    if (level(fit.monthlyPayment) >= 4) reasons.push('Low required monthly payment fits your top goal');
  } else if (goal === 'fast-payoff') {
    score += scaled(fit.payoffSpeed, 5) + scaled(fit.interestEfficiency, 3);
    if (level(fit.payoffSpeed) >= 4) reasons.push('Pays the loan down quickly, matching your payoff goal');
  } else if (goal === 'balanced') {
    score += scaled(fit.monthlyPayment, 3) + scaled(fit.payoffSpeed, 3);
    if (s.kind === 'fixed' && s.termYears >= 20 && s.termYears <= 25) {
      score += 8;
      reasons.push('Middle amortization balances payment and payoff');
    }
  } else if (goal === 'build-equity') {
    score += scaled(fit.equityBuild, 6);
    if (level(fit.equityBuild) >= 4) reasons.push('Builds equity quickly');
  } else if (goal === 'lowest-interest') {
    score += scaled(fit.interestEfficiency, 6);
    if (level(fit.interestEfficiency) >= 4) reasons.push('Strong lifetime-interest efficiency');
  } else if (goal === 'flexibility') {
    score += scaled(fit.monthlyPayment, 4);
    if (s.kind === 'fixed' && s.termYears === 30) {
      score += 8;
      reasons.push('Low required payment with the option to prepay on your schedule');
    }
  }

  // Voluntary extra payments make the 30-year's flexibility more valuable
  if ((p.plannedExtraPayments === 'monthly' || p.plannedExtraPayments === 'large-periodic') &&
      s.kind === 'fixed' && s.termYears === 30) {
    score += 6;
    reasons.push('Voluntary extra principal reaches faster payoff while keeping the required payment low');
  }

  // ── Payment stability preference ──────────────────────────────
  const stab = p.paymentStabilityPreference;
  if (stab === 'essential' || stab === 'important') {
    if (s.kind === 'fixed') {
      score += stab === 'essential' ? 10 : 6;
      reasons.push('Payment can never change — matches your stability preference');
    } else {
      score -= stab === 'essential' ? 25 : 12;
      cautions.push('Payment can change after the initial fixed period');
    }
  }

  // ── ARM / IO gating (never "short hold = ARM") ────────────────
  if (s.kind === 'arm' || s.kind === 'io' || r.requiresLivePricing) {
    pricingRequired = r.requiresLivePricing;
  }
  if (s.kind === 'arm') {
    const hold = p.expectedHoldYears;
    const maxHold = r.idealHoldMaxYears ?? (s.armInitialFixedYears ? s.armInitialFixedYears - 1 : null);
    if (hold === undefined) {
      score -= 8; // unknown horizon: fixed is the safer default
    } else if (maxHold != null && hold <= maxHold) {
      score += 10;
      reasons.push(`Your expected ${hold}-year hold fits inside the ${s.armInitialFixedYears}-year fixed period with a buffer`);
    } else {
      score -= 25;
      cautions.push('Expected time in the home extends past the initial fixed period — rate could reset while you still hold the loan');
    }
    if (p.rateRiskTolerance === 'no') {
      score -= 30;
      cautions.push('You indicated no tolerance for rate-reset risk');
    }
    cautions.push('Needs a live pricing comparison against the equivalent fixed rate before it can be recommended');
  }
  if (s.kind === 'io' || s.interestOnly === 'yes') {
    score -= 20;
    cautions.push('Interest-only period ends in a payment jump; requires an explicit cash-flow strategy');
  }

  // ── Affordability guardrail for shorter terms ────────────────
  const pi = screeningMonthlyPI(p.loanAmount, s.termYears);
  if (pi != null) {
    if (p.maxComfortablePayment) {
      if (pi > p.maxComfortablePayment * 1.15) {
        score -= 30;
        cautions.push('Required payment is likely well above your stated comfort level');
      } else if (pi > p.maxComfortablePayment) {
        score -= 15;
        cautions.push('Required payment may stretch past your stated comfort level');
      } else if (s.termYears <= 20 && (goal === 'fast-payoff' || goal === 'build-equity' || goal === 'lowest-interest')) {
        score += 8;
        reasons.push('Faster-payoff payment fits within your stated comfort level');
      }
    }
    if (p.annualIncome && p.monthlyDebts !== undefined) {
      const dti = ((pi + p.monthlyDebts) / (p.annualIncome / 12)) * 100;
      if (dti > 55) {
        score -= 30;
        cautions.push('Estimated payment plus debts is a very high share of income for this term');
      } else if (dti > 47) {
        score -= 12;
        cautions.push('Estimated debt-to-income runs high for this term');
      }
    }
  } else if (s.termYears && s.termYears <= 20) {
    // Can't verify affordability → don't boost aggressive terms
    score -= 4;
  }

  // ── Documentation / execution preferences ─────────────────────
  if (prod.transaction.streamline && p.loanPurpose === 'refinance' &&
      p.existingLoanType && prod.transaction.streamlineSourceFamily === p.existingLoanType) {
    score += 12;
    reasons.push('Streamline path — far less documentation than a standard refinance');
  }
  // Purpose-built manufactured-housing programs beat generic conditional MH
  if (prod.property.manufacturedSpecialty && p.propertyType === 'manufactured') {
    score += 8;
    reasons.push('Purpose-built for manufactured homes');
  }
  if (prod.family === 'va' && p.veteranStatus === 'eligible') {
    // The no-MI advantage matters most above 80% LTV; at 20%+ down a
    // conventional loan has no MI either and avoids the funding fee.
    if (p.ltv === undefined || p.ltv > 80) {
      score += 6;
      reasons.push('VA benefit: no down-payment pressure and no monthly mortgage insurance');
    } else {
      cautions.push('With 20%+ equity, compare the VA funding fee against a no-MI conventional loan');
    }
  }

  // FHA mortgage insurance premium is unavoidable and usually permanent —
  // penalize when the borrower's equity/credit make conventional cheaper.
  if (prod.family === 'fha' && !prod.transaction.streamline) {
    if (p.ltv !== undefined && p.ltv <= 80) {
      score -= 8;
      cautions.push('FHA charges mortgage insurance even with 20%+ equity — conventional avoids it');
    } else if (p.creditBand && p.creditBand.min >= 700) {
      score -= 4;
      cautions.push('With strong credit, conventional pricing usually beats FHA once MIP is included');
    }
  }

  // ── Standard-before-specialty & risk penalties ────────────────
  if (prod.family === 'nonqm') score -= 12;
  if (prod.active === 'archetype') score -= 4;
  if (prod.governance.overlayRisk === 'high') score -= 4;
  if (prod.governance.overlayRisk === 'very-high') score -= 8;
  if (prod.underwriting.manualUw === 'yes') score -= 2;

  if (r.caution) cautions.push(r.caution);

  // ── Confidence ────────────────────────────────────────────────
  let confidence = 0.95;
  confidence -= Math.min(evaluation.cautions.length * 0.05, 0.25);
  if (prod.active === 'archetype') confidence -= 0.15;
  if (evaluation.status === 'needs_data') confidence -= 0.1 * evaluation.missingInputs.length;
  if (p.creditUnsure) confidence -= 0.05;
  confidence = Math.max(0.3, Math.min(0.98, confidence));

  return {
    fitScore: Math.round(Math.max(0, Math.min(100, score))),
    confidence: Math.round(confidence * 100) / 100,
    fitReasons: reasons,
    fitCautions: [...new Set(cautions)],
    pricingRequired,
    pricing: pricingProvider && pricingProvider.available ? pricingProvider.getPricing(prod.termProgramId) : null,
  };
}
